import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { resetAnalyticsConsent } from "@/lib/analytics";

const CONTENT = {
  services: {
    kicker: "Services",
    title: "Everything GamePlat does",
    body: "Yorick is more than a chatbot — it's a full strategy platform. Here's what you get access to.",
    bullets: [
      "AI strategy coaching across roguelikes, card games and AAA blockbusters",
      "Campaign walkthroughs and hidden-collectible guides for single-player titles",
      "Meta breakdowns, build orders and combo trees updated as games evolve",
      "Fair-play focused tips for competitive multiplayer — no cheats, no bans",
    ],
  },
  status: {
    kicker: "System Status",
    title: "What's live right now",
    body: "The waitlist, accounts, Yorick chat, and Balatro guides are up. Live card charges stay in Square sandbox until a real non-admin purchase is proven. Discord is not connected yet. Game hubs: Balatro and GTA 6 are live; the Slay the Spire, Hades, Marvel Snap, Vampire Survivors and Battlefield 6 hubs are not published yet.",
    status: [
      ["Website", "Operational"],
      ["Waitlist & newsletter", "Operational"],
      ["Yorick chat", "Operational"],
      ["Balatro hub", "Operational"],
      ["GTA 6 hub", "Operational"],
      ["Slay the Spire hub", "Not working yet"],
      ["Hades hub", "Not working yet"],
      ["Marvel Snap hub", "Not working yet"],
      ["Vampire Survivors hub", "Not working yet"],
      ["Battlefield 6 hub", "Not working yet"],
      ["Payments (Square)", "Sandbox"],
      ["Discord", "Not connected"],
    ],
  },
  docs: {
    kicker: "Docs",
    title: "How GamePlat works",
    body: "Short version: buy Balatro from an official store, join our free waitlist, create an account, and ask Yorick when you get stuck.",
    bullets: [
      "December 12, 2026 is the Balatro Community Challenge — join the waitlist on Tournaments",
      "Yorick chat is the strategy coach. Pick Balatro or just type the question",
      "Guides live under /balatro — start with the beginner guide",
      "Paid plans are sandbox until a real non-admin purchase is proven. Rookie chat is free",
    ],
  },
  tutorials: {
    kicker: "Tutorials",
    title: "Learn Balatro, then compete",
    body: "The launch guides are live. Open one, then take it to Yorick or the waitlist.",
    bullets: [
      "Balatro beginner guide",
      "How Jokers work",
      "How to get past Ante 4",
      "How GamePlat tournaments work",
    ],
  },
  blog: {
    kicker: "News",
    title: "The GamePlat board",
    body: "Strategy posts and event notes live on News. Yorick also posts there when a new guide goes up.",
    bullets: [
      "Open News for the latest board posts",
      "Join the newsletter for one-shot alerts",
      "December 12, 2026 is on the calendar",
    ],
  },
  careers: {
    kicker: "Careers",
    title: "Not hiring yet",
    body: "GamePlat is a small launch. There are no open roles right now. If you want to compete, join the waitlist. If you need to reach us, use Support.",
    bullets: [
      "No job listings at the moment",
      "Players: join the December 12 waitlist",
      "Press or partners: use Support",
    ],
  },
  privacy: {
    kicker: "Privacy",
    title: "Privacy Policy",
    body: "We collect only the information needed to operate GamePlat, including account details, tournament waitlist entries, newsletter subscriptions, support messages, and conversations used to provide chat history and improve Yorick. We use submitted email addresses to send the updates requested through our forms and do not sell personal information. Optional Google Analytics loads only after a visitor accepts analytics and is used to understand traffic and measure waitlist or newsletter conversions without sending form entries or email addresses to Google. You can decline analytics, opt out of marketing updates, or request access, correction, or deletion through Support.",
  },
  terms: {
    kicker: "Terms",
    title: "Terms of Service",
    body: "GamePlatPlay provides independent game-strategy guidance that may be incomplete or wrong. Users must play fairly and may not use the service for cheating, credential theft, automation, code injection, or anti-cheat circumvention. Paid plans renew monthly until canceled; price, renewal, cancellation timing, and refund terms must be shown before checkout. GamePlatPlay is independent and is not affiliated with or endorsed by the publishers of the games discussed.",
  },
  cookies: {
    kicker: "Privacy",
    title: "Cookies and local storage",
    body: "GamePlatPlay uses an essential secure session cookie and local browser storage to keep users signed in and preserve necessary application state. Non-essential advertising or analytics tracking must not be enabled until the consent and privacy disclosures are configured.",
  },
  refunds: {
    kicker: "Billing",
    title: "Cancellation and refund notice",
    body: "Paid plans are billed monthly by Square and continue until canceled. Cancellation is intended to stop future renewals while access continues through the paid period. Refund eligibility, timing, support contact, and any exceptions must be finalized and displayed before live checkout opens.",
  },
  community: {
    kicker: "Community",
    title: "Community rules",
    body: "Be respectful, compete fairly, submit truthful information, respect intellectual property, and do not harass others or request cheats, hacks, credential theft, automation, or anti-cheat bypasses. GamePlatPlay may restrict accounts that threaten users, the service, or fair play.",
  },
  refer: {
    kicker: "Refer a friend",
    title: "Referral rewards are not live yet",
    body: "There is no unique referral link yet. Tell a friend about the December 12 waitlist and Yorick. Paid-plan referral credit will wait until live Square purchases are proven.",
    bullets: [
      "Share the waitlist at /tournaments",
      "Share a Balatro guide if they are new",
      "Referral months are not active until live billing is on",
    ],
  },
};

export default function StaticPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const data = CONTENT[slug];

  if (!data) {
    return (
      <div className="mx-auto max-w-3xl px-5 py-28 text-center">
        <p className="font-vt323 text-2xl text-gp-gold tracking-widest">404</p>
        <h1 className="font-unbounded font-black tracking-tighter text-4xl mt-3">Yorick can't find that page</h1>
        <p className="text-white/60 mt-4">The card you're looking for isn't in this deck.</p>
        <Button onClick={() => navigate("/")} className="mt-8 rounded-full bg-gp-blue">Back home</Button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl px-5 md:px-8 py-16 md:py-20" data-testid={`static-${slug}`}>
      <span className="font-vt323 uppercase tracking-widest text-lg text-gp-blue">{data.kicker}</span>
      <h1 className="font-unbounded font-black tracking-tighter text-4xl sm:text-5xl mt-2">{data.title}</h1>
      <p className="text-white/70 text-lg mt-5 leading-relaxed max-w-2xl">{data.body}</p>

      {data.bullets && (
        <ul className="mt-8 grid sm:grid-cols-2 gap-4">
          {data.bullets.map((b) => (
            <li key={b} className="glass rounded-2xl p-5 flex gap-3">
              <CheckCircle2 size={20} className="text-gp-gold shrink-0 mt-0.5" />
              <span className="text-white/85">{b}</span>
            </li>
          ))}
        </ul>
      )}

      {data.status && (
        <div className="mt-8 glass rounded-2xl divide-y divide-white/10">
          {data.status.map(([name, s]) => (
            <div key={name} className="flex items-center justify-between px-6 py-4">
              <span className="text-white/85">{name}</span>
              <span className={`flex items-center gap-2 text-sm ${s === "Operational" ? "text-gp-green" : "text-amber-300"}`}>
                <span className={`h-2 w-2 rounded-full ${s === "Operational" ? "bg-gp-green animate-pulse-glow" : "bg-amber-300"}`} /> {s}
              </span>
            </div>
          ))}
        </div>
      )}

      {slug === "privacy" && (
        <Button
          type="button"
          variant="outline"
          onClick={() => { resetAnalyticsConsent(); window.location.reload(); }}
          className="mt-8 rounded-full border-white/20"
        >
          Review analytics choice
        </Button>
      )}

      <div className="mt-12 flex flex-wrap gap-4">
        <Button onClick={() => navigate("/chat")} className="rounded-full bg-gp-gold text-gp-bg font-bold px-6">
          Ask Yorick <ArrowRight size={16} className="ml-2" />
        </Button>
        <Link to="/support" className="text-white/60 hover:text-white flex items-center gap-2 self-center">Contact support</Link>
      </div>
    </div>
  );
}
